import { generateID, objectFilter, nullishFilter } from '@caresend/utils';
import isEqual from 'lodash.isequal';
import { watch, ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouteHeading } from './routeHeading.js';

/**
 * Not needed after Vue 3.4 - After upgrade, include `{ once: true }` in 3rd
 * (options) arg.
 */
const watchOnce = (value, callback, options) => {
    const unwatch = watch(value, (...args) => {
        callback(...args);
        unwatch();
    }, options);
};
/**
 * Watch a value until some condition is met. By default, the watch stops when
 * the value is defined.
 */
const watchUntil = (value, callback, until = (v) => !!v) => {
    const unwatch = watch(value, (...args) => {
        if (!until(args[0]))
            return;
        callback(...args);
        unwatch();
    });
};
/**
 * Return a computed value which hydrates its true value after a delay if
 * the value is not already hydrated.
 *
 * Useful for delaying a value that loading immediately can result in choppy
 * animations.
 */
const delayedComputed = (value, delay = 500) => {
    const result = ref();
    const setResult = (newVal) => {
        result.value = newVal;
    };
    const withDelay = (newVal) => setTimeout(() => setResult(newVal), delay);
    /** If the value doesn't exist on the initial call, our goal is to delay setting the value */
    const needsDelay = !value();
    watch(value, (newVal) => {
        if (needsDelay)
            withDelay(newVal);
        else
            setResult(newVal);
    }, { immediate: true, deep: true });
    return result;
};
const debouncedComputed = (value, delay = 500) => {
    const result = ref();
    const setResult = (newVal) => {
        result.value = newVal;
    };
    const withDelay = (newVal) => setTimeout(() => setResult(newVal), delay);
    const needsDelay = !value();
    let timeout = null;
    watch(value, (newVal) => {
        if (needsDelay) {
            if (timeout)
                clearTimeout(timeout);
            timeout = withDelay(newVal);
        }
        else
            setResult(newVal);
    }, { immediate: true, deep: true });
    return result;
};
const getElementAutoSize = (element) => {
    const parent = element.parentElement;
    if (!parent)
        return { width: 0, height: 0 };
    const copy = element.cloneNode(true);
    copy.style.width = 'auto';
    // copy.style.maxWidth = 'fit-content';
    copy.style.minWidth = 'fit-content';
    copy.style.height = 'auto';
    // copy.style.maxHeight = 'fit-content';
    copy.style.minHeight = 'fit-content';
    copy.style.opacity = '0';
    parent.appendChild(copy);
    const { width, height } = copy.getBoundingClientRect();
    parent.removeChild(copy);
    return { width, height };
};
/** Elements registered for resize and mutation observers */
const subscribedElements = new Map();
/**
 * Threshold for difference between new/old height and new/old width to exceed
 * for skipping observer updates. A full integer pixel change is probably
 * overkill, but might as well.
 */
const threshold = 1;
const shouldNotUpdate = (newVal, oldVal) => Math.abs(newVal - oldVal) < threshold;
/** Callback for ResizeObserver */
const observeCallback = (entries) => {
    entries.forEach((entry) => {
        var _a;
        if (entry.target instanceof Element) {
            const contentRect = entry.target.getBoundingClientRect();
            Object.values((_a = subscribedElements.get(entry.target)) !== null && _a !== void 0 ? _a : {})
                .forEach((callback) => callback(contentRect));
        }
        else {
            console.error('Cannot observe non-Element target', entry.target);
        }
    });
};
let resizeObserver = null;
let mutationObserver = null;
const disconnectObserver = (element, options) => {
    if (!element)
        return;
    // Remove this specific callback from the element's callbacks
    const elementCallbacks = subscribedElements.get(element);
    if (elementCallbacks) {
        // Find and remove our callback
        const callbackIds = Object.keys(elementCallbacks);
        callbackIds.forEach((id) => {
            delete elementCallbacks[id];
        });
        if (Object.keys(elementCallbacks).length === 0) {
            // If no callbacks remain for this element, remove it from the map
            subscribedElements.delete(element);
            // Unobserve this element from observer, MutationObserver is not supported
            // to disconnect this way.
            if (options === null || options === void 0 ? void 0 : options.resize)
                resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.unobserve(element);
        }
        else {
            // Update the callbacks for this element
            subscribedElements.set(element, elementCallbacks);
        }
    }
    // If no elements remain subscribed, disconnect the observers
    if (subscribedElements.size === 0) {
        resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        mutationObserver === null || mutationObserver === void 0 ? void 0 : mutationObserver.disconnect();
        resizeObserver = null;
        mutationObserver = null;
    }
};
/** This allows maintaining a single observer for multiple DOM elements */
const observe = (element, callback, 
// By default, we want to observe both resize and mutation
options) => {
    var _a;
    if (!element)
        return;
    const id = generateID();
    const callbacks = (_a = subscribedElements.get(element)) !== null && _a !== void 0 ? _a : {};
    subscribedElements.set(element, { ...callbacks, [id]: callback });
    if (!resizeObserver) {
        resizeObserver = new ResizeObserver(observeCallback);
    }
    if (!mutationObserver) {
        mutationObserver = new MutationObserver(observeCallback);
    }
    /**
     * When an observer already exists, invoke the callback
     */
    const defaultCallback = () => {
        observeCallback([{
                target: element,
            }]);
    };
    if ((options === null || options === void 0 ? void 0 : options.resize) && resizeObserver) {
        resizeObserver.observe(element);
        defaultCallback();
    }
    if ((options === null || options === void 0 ? void 0 : options.mutation) && mutationObserver) {
        const mutationOptions = typeof options.mutation === 'boolean'
            ? { attributes: true }
            : options.mutation;
        mutationObserver.observe(element, mutationOptions);
        defaultCallback();
    }
    if (!resizeObserver && !mutationObserver) {
        console.error('No observers found');
    }
    return () => {
        disconnectObserver(element, options);
    };
};
const useElementDimensions = (element, options) => {
    const width = ref(0);
    const height = ref(0);
    const top = ref(0);
    const left = ref(0);
    let disconnect = () => { };
    let currentElement = null;
    let currentOptions;
    const initObserver = () => {
        var _a, _b;
        if (!element.value)
            return;
        // Store references for disconnect
        currentElement = element.value;
        currentOptions = options;
        const { width: autoWidth, height: autoHeight, } = getElementAutoSize(element.value);
        width.value = autoWidth;
        height.value = autoHeight;
        top.value = element.value.getBoundingClientRect().top;
        left.value = element.value.getBoundingClientRect().left;
        try {
            disconnect = observe(element.value, (contentRect) => {
                var _a, _b, _c, _d;
                if (shouldNotUpdate(contentRect.width, width.value)
                    && shouldNotUpdate(contentRect.height, height.value)
                    && shouldNotUpdate(contentRect.top, top.value)
                    && shouldNotUpdate(contentRect.left, left.value))
                    return;
                width.value = Math.round(contentRect.width);
                height.value = Math.round(contentRect.height);
                top.value = (_b = (_a = element.value) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) !== null && _b !== void 0 ? _b : contentRect.top;
                left.value = (_d = (_c = element.value) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().left) !== null && _d !== void 0 ? _d : contentRect.left;
            }, {
                resize: (_a = options === null || options === void 0 ? void 0 : options.resize) !== null && _a !== void 0 ? _a : true,
                mutation: (_b = options === null || options === void 0 ? void 0 : options.mutation) !== null && _b !== void 0 ? _b : true,
            }) || disconnect;
        }
        catch (error) {
            console.error('Error initializing observers', error);
        }
    };
    /**
     * Make sure the observer is initialized only after
     * the element is rendered, i.e the ref is hydrated.
     */
    watch(element, (newEl, oldEl) => {
        if (!newEl && !oldEl)
            return;
        if (!!newEl === !oldEl) {
            initObserver();
        }
        else if (oldEl && !newEl) {
            disconnectObserver(currentElement, currentOptions);
        }
    }, { immediate: true });
    if (options === null || options === void 0 ? void 0 : options.forceObserveRef) {
        watch(options.forceObserveRef, (forceObserve) => {
            if (forceObserve && element.value) {
                initObserver();
            }
            else {
                disconnectObserver(currentElement, currentOptions);
            }
        });
    }
    return {
        disconnect: disconnectObserver,
        height,
        width,
        top,
        left,
    };
};
const useElementChildren = (element, childSelectors) => {
    const children = ref({});
    const checkForChildren = () => {
        if (!element.value)
            return;
        const newChildren = objectFilter(Object.fromEntries(childSelectors.map((selector) => {
            var _a;
            return [
                selector,
                (_a = element.value) === null || _a === void 0 ? void 0 : _a.querySelector(selector),
            ];
        })), nullishFilter);
        children.value = newChildren;
    };
    const initObserver = () => {
        if (!element.value)
            return;
        observe(element.value, () => checkForChildren(), {
            mutation: {
                childList: true,
                subtree: true,
            },
        });
    };
    /** Make sure the observer is initialized only after the element is rendered */
    watchUntil(element, () => {
        initObserver();
    });
    return { children };
};
const mediaQueries = {
    mobile: '(max-width: 767px)',
    tablet: '(min-width: 768px) and (max-width: 1023px)',
    desktop: '(min-width: 1024px)',
};
/**
 * Watch a media query.
 *
 * @param query - A media query string or a shorthand query.
 * @returns A ref that tracks the media query match status.
 */
const useMediaQuery = (query) => {
    var _a;
    const matches = ref(false);
    const mediaQueryString = (_a = mediaQueries[query]) !== null && _a !== void 0 ? _a : query;
    const updateMatches = () => {
        matches.value = window.matchMedia(mediaQueryString).matches;
    };
    onMounted(() => {
        updateMatches();
        window.addEventListener('resize', updateMatches);
    });
    onUnmounted(() => {
        window.removeEventListener('resize', updateMatches);
    });
    return { matches };
};
/**
 * Watch the height of an element. The element must have rendered prior to
 * calling this, running this within onMounted is suggested.
 */
const useElementHeight = (
/** Selector for `document.querySelector` to find the first matching element. */
selector) => {
    const elementHeight = ref(0);
    let currentObserver = null;
    const disconnectElementObserver = () => {
        if (currentObserver) {
            currentObserver.disconnect();
            currentObserver = null;
        }
    };
    const initObserver = (selectorValue) => {
        disconnectElementObserver();
        currentObserver = new ResizeObserver((entries) => {
            var _a;
            const firstEntry = entries[0];
            if (!firstEntry) {
                console.error(`Unable to watch height of ${selectorValue} due to missing data.`);
                return;
            }
            if (firstEntry.borderBoxSize || firstEntry.contentRect) {
                /** Includes padding */
                const borderBoxHeight = (_a = firstEntry.borderBoxSize[0]) === null || _a === void 0 ? void 0 : _a.blockSize;
                elementHeight.value = borderBoxHeight !== null && borderBoxHeight !== void 0 ? borderBoxHeight : 0;
            }
        });
        const element = document.querySelector(selectorValue);
        if (element) {
            currentObserver.observe(element);
        }
        else {
            elementHeight.value = 0;
        }
    };
    watch(selector, (newSelector) => {
        initObserver(newSelector);
    }, { immediate: true });
    onUnmounted(() => {
        disconnectElementObserver();
    });
    return { elementHeight, disconnect: disconnectElementObserver };
};
/**
 * Watch the height of the navbar. The element must have rendered prior to
 * calling this, running this within onMounted is suggested. NavBar must have
 * `data-is-navbar` attribute.
 */
const useNavBarHeight = () => {
    const { heading } = useRouteHeading();
    const selector = computed(() => {
        var _a;
        return ((_a = heading.value) === null || _a === void 0 ? void 0 : _a.disableStickyHeader)
            ? '[data-is-navbar-bar]'
            : '[data-is-navbar]';
    });
    const { elementHeight: navBarHeight, disconnect } = useElementHeight(selector);
    /**
     * Watch NavBar height (including header if applicable), and adjust content
     * margin so that content sits directly under NavBar.
     *
     * If sticky header is disabled, this function will:
     * - Not set/unset margin on the passed element if `isHeader` is false
     * - Set/unset margin on the header element if `isHeader` is true
     *
     * Place this in onMounted to ensure the initial adjustment works.
     * use syncContentTopWithNavBarHeight for sticky content.
     */
    const syncContentMarginWithNavBarHeight = (contentElement, options = {}) => {
        const { force, isVisibleOverride, isHeader } = options;
        watch([
            () => navBarHeight.value,
            () => isVisibleOverride === null || isVisibleOverride === void 0 ? void 0 : isVisibleOverride.value,
            () => { var _a; return (_a = heading === null || heading === void 0 ? void 0 : heading.value) === null || _a === void 0 ? void 0 : _a.disableStickyHeader; },
            () => contentElement.value,
        ], ([newNavBarHeight, newIsVisibleOverride, newDisableStickyHeader, newContentElement,]) => {
            const contentElementStyle = newContentElement === null || newContentElement === void 0 ? void 0 : newContentElement.style;
            if (contentElementStyle) {
                const navHeight = newIsVisibleOverride === false ? 0 : newNavBarHeight;
                const shouldSetMargin = (force
                    || (isHeader && newDisableStickyHeader)
                    || (!isHeader && !newDisableStickyHeader));
                const marginToSet = shouldSetMargin ? `${navHeight}px` : '';
                contentElementStyle.marginTop = marginToSet;
            }
        }, { immediate: true });
    };
    const syncContentTopWithNavBarHeight = (contentElement, options = {}) => {
        const { force, isVisibleOverride, isHeader } = options;
        watch([
            () => navBarHeight.value,
            () => isVisibleOverride === null || isVisibleOverride === void 0 ? void 0 : isVisibleOverride.value,
            () => { var _a; return (_a = heading === null || heading === void 0 ? void 0 : heading.value) === null || _a === void 0 ? void 0 : _a.disableStickyHeader; },
            () => contentElement.value,
        ], ([newNavBarHeight, newIsVisibleOverride, newDisableStickyHeader, newContentElement,]) => {
            const contentElementStyle = newContentElement === null || newContentElement === void 0 ? void 0 : newContentElement.style;
            if (contentElementStyle) {
                const navHeight = newIsVisibleOverride === false ? 0 : newNavBarHeight;
                const shouldSetTop = (force
                    || (isHeader && newDisableStickyHeader)
                    || (!isHeader && !newDisableStickyHeader));
                const topToSet = shouldSetTop ? `${navHeight}px` : '';
                contentElementStyle.top = topToSet;
            }
        }, { immediate: true });
    };
    /**
     * Maximize view height to fill remaining space under NavBar
     *
     * Only suitable for use with full page content.
     */
    const syncContentHeightWithNavBarHeight = (contentElement, options = {}) => {
        const { force, isVisibleOverride, isHeader } = options;
        if (isHeader) {
            console.error('syncContentHeightWithNavBarHeight is not suitable for use with header');
            return;
        }
        watch([
            () => navBarHeight.value,
            () => isVisibleOverride === null || isVisibleOverride === void 0 ? void 0 : isVisibleOverride.value,
            () => { var _a; return (_a = heading === null || heading === void 0 ? void 0 : heading.value) === null || _a === void 0 ? void 0 : _a.disableStickyHeader; },
            () => contentElement.value,
        ], ([newNavBarHeight, newIsVisibleOverride, newDisableStickyHeader, newContentElement,]) => {
            const contentElementStyle = newContentElement === null || newContentElement === void 0 ? void 0 : newContentElement.style;
            if (contentElementStyle) {
                const navHeight = newIsVisibleOverride === false ? 0 : newNavBarHeight;
                const shouldSetHeight = force || !newDisableStickyHeader;
                const heightToSet = shouldSetHeight ? `calc(100dvh - ${navHeight}px)` : '';
                contentElementStyle.height = heightToSet;
            }
        }, { immediate: true });
    };
    return {
        disconnect,
        navBarHeight,
        syncContentHeightWithNavBarHeight,
        syncContentMarginWithNavBarHeight,
        syncContentTopWithNavBarHeight,
    };
};
/**
 * Watch the height of FixedFooterButton. The element must have rendered prior
 * to calling this, running this within onMounted is suggested.
 *
 * Currently only supports Header with `globalFooter: true`. Potential future
 * improvement could be to watch for addition/removal of FixedFooterButton or
 * to always render FixedFooterButton at 0 height when Header is rendered, to
 * add support for `mobileFooter: true`.
 */
const useFooterHeight = () => {
    const selector = ref('[data-is-footer]');
    const { elementHeight: footerHeight, disconnect } = useElementHeight(selector);
    /**
     * Watch NavBar height (including header if applicable), and adjust content
     * margin so that content sits directly under NavBar.
     *
     * Place this in onMounted to ensure the initial adjustment works.
     * use syncContentTopWithNavBarHeight for sticky content.
     */
    const syncContentPaddingWithFooterHeight = (contentElement, additionalPadding = 0) => watch(() => footerHeight.value, () => {
        var _a;
        const contentElementStyle = (_a = contentElement.value) === null || _a === void 0 ? void 0 : _a.style;
        if (contentElementStyle && footerHeight.value) {
            const padding = footerHeight.value + additionalPadding;
            contentElementStyle.paddingBottom = `${padding}px`;
        }
    }, { immediate: true });
    return {
        disconnect,
        footerHeight,
        syncContentPaddingWithFooterHeight,
    };
};
/**
 * Get the position of an element relative to its nearest parent. This is
 * not possible with the native `getBoundingClientRect` method, so we
 * recursively check each parent element until we find one that is a table
 * element, at which point we use the offset present in the table element.
 */
const getElementRelativePosition = (element) => {
    if (!element)
        return { x: 0, y: 0 };
    const { x, y } = element.getBoundingClientRect();
    const parent = element.parentElement;
    if (parent) {
        const { x: parentX, y: parentY } = getElementRelativePosition(parent);
        return { x: x - parentX, y: y - parentY };
    }
    return { x, y };
};
/**
 * Get the nearest ancestor element that matches a given selector.
 */
const getElementAncestor = (element, selector) => {
    let ancestor = element;
    while (ancestor && !ancestor.matches(selector)) {
        ancestor = ancestor.parentElement;
    }
    return ancestor;
};
const getArea = (element) => element.clientWidth * element.clientHeight;
/* eslint-disable no-param-reassign */
const getLargestAncestor = (element) => {
    if (!element)
        return null;
    let ancestor = element;
    const area = getArea(ancestor);
    while ((ancestor === null || ancestor === void 0 ? void 0 : ancestor.parentElement) && getArea(ancestor.parentElement) > area) {
        ancestor = ancestor.parentElement;
    }
    return ancestor;
};
/* eslint-enable no-param-reassign */
/**
 * Maintain syncronocity between a prop and a local ref.
 *
 * @pumposh I do this so often I'm just gonna do this
 */
const propSync = (prop, emit, localRef) => {
    const local = localRef !== null && localRef !== void 0 ? localRef : ref(prop());
    watch(prop, (value) => {
        if (isEqual(value, local.value))
            return;
        local.value = value;
    }, { immediate: true, deep: true });
    watch(local, (value) => {
        if (isEqual(value, prop()))
            return;
        emit(value);
    }, { immediate: true, deep: true });
    return local;
};

export { debouncedComputed, delayedComputed, getElementAncestor, getElementAutoSize, getElementRelativePosition, getLargestAncestor, propSync, useElementChildren, useElementDimensions, useElementHeight, useFooterHeight, useMediaQuery, useNavBarHeight, watchOnce, watchUntil };
